import { Trans } from "@lingui/react/macro";
import React, { useCallback, useState } from "react";
import {
  Button,
  ButtonProps,
  ButtonVariant,
  IconName
} from "@silversea-ssc/ui-components-react";
import {
  AnchorCtaFragment,
  BrochureCtaFragment,
  ButtonCtaFragment,
  FileCtaFragment,
  FormDisclaimerFragment,
  LinkCtaFragment,
  PhoneNumberCtaFragment,
  RaqCtaFragment,
  Scalars,
  VideoCtaFragment
} from "types/graphql-types";
import PageBuilderPageCTAButton from "components/PageBuilder/PageBuilderPageCTAButton";

import {
  getAllAnchorableSections,
  getAnchorableSectionByRefName,
  scrollToSection
} from "../../utils/inPageNavigation";
import { isPresent } from "src/utils/checks";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import {
  useBrochureThankYouPageURL,
  useTargetBrochure
} from "src/utils/brochures";
import { usePageType } from "./usePageType";
import { getCruiseSpecificRAQ } from "src/utils/requestAQuoteHelper";
import { match } from "ts-pattern";
import { useOverridePhoneNumberInABTest } from "src/inbound-ab-test-experiment/phone";
import FycLinkCTAButtonProps from "./fyc/SanityFycLinkCTA";
import { toButtonVariantProps, toHeroCTAButtonVariant } from "./buttons";

const VideoModal = React.lazy(() => import("components/Video/VideoModal"));
const NewRequestBrochureModal = React.lazy(
  () => import("components/RequestBrochureModal/RequestBrochureModal")
);

export type SharedButtonProps = Pick<
  ButtonProps,
  "iconName" | "size" | "mobileSize" | "full" | "noWrap" | "inverted"
> & { defaultVariant?: ButtonVariant };

export type PageBuilderButtonCTAProps = Partial<ButtonCtaFragment> &
  Omit<SharedButtonProps, "variant"> & {
    defaultButtonVariant?: ButtonVariant;
  } & {
    forHero?: "normal" | "inverted";
  };

const defaultIconForType = (
  type: Scalars["SanityButtonType"]["output"] | ButtonVariant
): IconName | undefined => {
  if (type == "link" || type == "text") return "arrow-right";
  return undefined;
};

type RaqCTAButtonProps = RaqCtaFragment & SharedButtonProps;
const RaqCTAButton: React.FC<RaqCTAButtonProps> = ({
  defaultFullPath,
  variant,
  label,
  cruise,
  defaultVariant = "primary",
  ...props
}) => {
  const cruiseCode = cruise?.data?.cruiseCode;

  const path =
    cruiseCode && defaultFullPath
      ? getCruiseSpecificRAQ(defaultFullPath, cruiseCode)
      : defaultFullPath;
  const defaultCTALabel = <Trans>Request a Quote</Trans>;
  const variantOrDefault = toButtonVariantProps(variant, defaultVariant);

  return (
    <Button
      {...props}
      {...variantOrDefault}
      renderAs="a"
      href={path}
      dataContentPiece="request_a_quote"
    >
      {label?.localized || defaultCTALabel}
    </Button>
  );
};

type BrochureCTAButtonProps = Omit<BrochureCtaFragment, "asModal"> &
  SharedButtonProps & {
    iconName?: IconName;
  };
const BrochureCTAButton: React.FC<BrochureCTAButtonProps> = ({
  brochure,
  defaultFullPath,
  variant,
  defaultVariant = "primary",
  label,
  ...props
}) => {
  const defaultCTALabel = <Trans>View the Brochures</Trans>;
  const link = brochure?.fullPath || defaultFullPath;
  const variantOrDefault = toButtonVariantProps(variant, defaultVariant);
  return (
    <Button
      {...props}
      renderAs="a"
      href={link}
      {...variantOrDefault}
      dataContentPiece="view_the_brochures"
    >
      {label?.localized || defaultCTALabel}
    </Button>
  );
};
type BrochureCTAButtonWithModalProps = Omit<BrochureCtaFragment, "asModal"> & {
  iconName?: IconName;
  formDisclaimerContent: FormDisclaimerFragment | undefined;
} & SharedButtonProps;
const BrochureCTAButtonWithModal: React.FC<BrochureCTAButtonWithModalProps> = ({
  brochure,
  variant,
  defaultVariant = "primary",
  label,
  formDisclaimerContent,
  ...props
}) => {
  const [showModal, setShowModal] = useState(false);
  const geolocation = useGeolocation();
  const language = useLanguage();
  const pageType = usePageType();
  const brochures = brochure?.brochures?.filter(isPresent) || [];
  const targetedBrochure = useTargetBrochure(brochures, geolocation, language);
  const openBrochureModal = useCallback(() => setShowModal(true), []);
  const thankYouPageURL = useBrochureThankYouPageURL(language, pageType);

  if (!targetedBrochure) return null;

  // const urls = useStaticUrls();
  const defaultCTALabel = <Trans>View the Brochures</Trans>;
  // const link = props.brochure?.fullPath || urls.brochures;
  const variantOrDefault = toButtonVariantProps(variant, defaultVariant);

  return (
    <React.Fragment>
      <Button
        {...props}
        onClick={openBrochureModal}
        renderAs="button"
        {...variantOrDefault}
        dataContentPiece="view_brochure_online"
        iconName={props.iconName}
      >
        {label?.localized || defaultCTALabel}
      </Button>
      {showModal && thankYouPageURL && (
        <React.Suspense fallback={<div></div>}>
          <NewRequestBrochureModal
            isOpen={true}
            onRequestClose={() => setShowModal(false)}
            brochure={targetedBrochure}
            thankYouStragegy={
              pageType == "LANDING_PAGE" ? "newPage" : "redirect"
            }
            thankYouPageURL={thankYouPageURL}
            formDisclaimerContent={formDisclaimerContent}
          />
        </React.Suspense>
      )}
    </React.Fragment>
  );
};

type FileCTAButtonProps = FileCtaFragment & SharedButtonProps;
const FileCTAButton: React.FC<FileCTAButtonProps> = ({
  variant,
  file,
  defaultVariant,
  label
}) => {
  const fileUrl = file?.localized?.asset?.url;
  const defaultCTALabel = <Trans id="cta-download">Download</Trans>;
  const variantOrDefault = toButtonVariantProps(variant, defaultVariant);
  return (
    <Button
      renderAs="a"
      {...variantOrDefault}
      href={`${fileUrl}?dl=`}
      iconName="download"
      rel="nofollow noopener"
      dataContentPiece="download"
      download
    >
      {label?.localized || defaultCTALabel}
    </Button>
  );
};

type LinkCTAButtonProps = LinkCtaFragment & SharedButtonProps;
const LinkCTAButton: React.FC<LinkCTAButtonProps> = ({
  url,
  variant,
  defaultVariant = "primary",
  openInNewTab,
  label,
  ...props
}) => {
  const localizedUrl = url?.localized;
  const defaultCTALabel = <Trans id="cta-discover-more">Discover more</Trans>;
  const variantOrDefault = toButtonVariantProps(variant, defaultVariant);
  const target = openInNewTab ? "_blank" : undefined;

  return (
    <Button
      {...props}
      renderAs="a"
      {...variantOrDefault}
      href={localizedUrl}
      dataContentPiece="discover_more"
      target={target}
    >
      {label?.localized || defaultCTALabel}
    </Button>
  );
};

type PhoneNumberCTAButtonProps = PhoneNumberCtaFragment & SharedButtonProps;
const PhoneNumberCTAButton: React.FC<PhoneNumberCTAButtonProps> = ({
  variant,
  defaultVariant = "primary",
  label,
  ...props
}) => {
  const phoneNumber = useOverridePhoneNumberInABTest();
  const defaultCTALabel = <Trans>Talk to an Expert</Trans>;

  const phoneNumberForLink = phoneNumber?.replace(/[^\d+]/g, "");
  const labelWithPhoneNumber = label?.localized?.replace(
    "$phoneNumber",
    phoneNumber || ""
  );

  const url = `tel:${phoneNumberForLink}`;
  const variantOrDefault = toButtonVariantProps(variant, defaultVariant);

  return (
    <Button
      {...props}
      renderAs="a"
      {...variantOrDefault}
      href={url}
      aria-label={`call ${phoneNumber}`}
      dataContentPiece="phone"
      iconName="circle-phone"
      iconPosition="left"
    >
      {labelWithPhoneNumber || defaultCTALabel}
    </Button>
  );
};

type AnchorCTAButtonProps = AnchorCtaFragment & SharedButtonProps;
const AnchorCTAButton: React.FC<AnchorCTAButtonProps> = ({
  variant,
  defaultVariant = "primary",
  target,
  label,
  ...props
}) => {
  const variantOrDefault = toButtonVariantProps(variant, defaultVariant);

  const onClick = useCallback(() => {
    const navContainerItems = getAllAnchorableSections();
    const ref = getAnchorableSectionByRefName(navContainerItems, target);

    if (ref) scrollToSection(ref);
  }, [target]);

  return (
    <Button
      {...props}
      renderAs="button"
      {...variantOrDefault}
      dataContentPiece="anchor_button"
      onClick={onClick}
    >
      {label?.localized}
    </Button>
  );
};

type VideoCTAButtonProps = VideoCtaFragment & SharedButtonProps;
const VideoCTAButton: React.FC<VideoCTAButtonProps> = ({
  video,
  label,
  variant,
  defaultVariant = "primary",
  ...props
}) => {
  const defaultCTALabel = <Trans id="watch the video">watch the video</Trans>;
  const variantOrDefault = toButtonVariantProps(variant, defaultVariant);
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  if (!video?.file) return null;

  return (
    <React.Fragment>
      {isVideoOpen && (
        <React.Suspense fallback={<div></div>}>
          <VideoModal
            // eslint-disable-next-line react/jsx-no-bind
            onRequestClose={() => setIsVideoOpen(false)}
            videoFile={video.file}
            trackingTitle={video.trackingTitle}
          />
        </React.Suspense>
      )}
      <Button
        {...props}
        renderAs="button"
        // eslint-disable-next-line react/jsx-no-bind
        onClick={() => setIsVideoOpen(true)}
        {...variantOrDefault}
        iconName="play"
        dataContentPiece="watch_the_video"
      >
        {label?.localized || defaultCTALabel}
      </Button>
    </React.Fragment>
  );
};

const PageBuilderButtonCTA: React.FC<
  React.PropsWithChildren<PageBuilderButtonCTAProps>
> = ({ forHero, ...originalProps }) => {
  const props = forHero
    ? toHeroCTAButtonVariant(originalProps, forHero == "inverted")
    : originalProps;
  const { defaultButtonVariant = "primary" } = props;

  const variant = "variant" in props ? props.variant : undefined;
  const buttonVariant = variant || defaultButtonVariant;
  const iconName = props.iconName ?? defaultIconForType(buttonVariant);

  return match(props)
    .with({ __typename: "SanityPageCTA" }, (props) => {
      return (
        <PageBuilderPageCTAButton
          {...props}
          variant={variant}
          defaultVariant={defaultButtonVariant}
        />
      );
    })
    .with({ __typename: "SanityFycLinkCTA" }, (props) => {
      return (
        <FycLinkCTAButtonProps
          {...props}
          iconName={iconName}
          variant={variant}
          defaultVariant={defaultButtonVariant}
        />
      );
    })
    .with({ __typename: "SanityRaqCTA" }, (props) => {
      return (
        <RaqCTAButton
          {...props}
          iconName={iconName}
          variant={variant}
          defaultVariant={defaultButtonVariant}
        />
      );
    })
    .with({ __typename: "SanityBrochureCTA" }, (props) => {
      const {
        asModal,
        defaultFullPath,
        formDisclaimerContent,
        ...specificProps
      } = props;
      return (
        <React.Fragment>
          {asModal ? (
            <BrochureCTAButtonWithModal
              {...specificProps}
              formDisclaimerContent={formDisclaimerContent}
              iconName="brochure"
              variant={variant}
              defaultVariant={defaultButtonVariant}
            />
          ) : (
            <BrochureCTAButton
              {...specificProps}
              defaultFullPath={defaultFullPath}
              iconName="brochure"
              variant={variant}
              defaultVariant={defaultButtonVariant}
            />
          )}
        </React.Fragment>
      );
    })
    .with({ __typename: "SanityFileCTA" }, (props) => {
      return (
        <FileCTAButton
          {...props}
          variant={variant}
          defaultVariant={defaultButtonVariant}
        />
      );
    })
    .with({ __typename: "SanityVideoCTA" }, (props) => {
      return (
        <VideoCTAButton
          {...props}
          variant={variant}
          defaultVariant={defaultButtonVariant}
        />
      );
    })
    .with({ __typename: "SanityLinkCTA" }, (props) => {
      return (
        <LinkCTAButton
          {...props}
          iconName={iconName}
          variant={variant}
          defaultVariant={defaultButtonVariant}
        />
      );
    })
    .with({ __typename: "SanityAnchorCTA" }, (props) => {
      return (
        <AnchorCTAButton
          {...props}
          iconName={iconName}
          variant={variant}
          defaultVariant={defaultButtonVariant}
        />
      );
    })
    .with({ __typename: "SanityPhoneNumberCTA" }, (props) => {
      return (
        <PhoneNumberCTAButton
          {...props}
          variant={variant}
          defaultVariant={defaultButtonVariant}
        />
      );
    })
    .otherwise(() => null);
};

export default PageBuilderButtonCTA;
